/**
 * Reportes → Compras por producto (`/reportes/compra-productos`).
 *
 * Puerto de `Reportes::compra_productos` + `Reportes::get_reporte_compras_productos_data`
 * + `Compra_model::get_reporte_compras_productos` +
 * `views/menu/reportes/compra_productos.php`.
 *
 * La pantalla del panel anterior tenía: filtros (fechas de emisión, estado,
 * proveedor y producto) con *Buscar*, *Limpiar*, *Excel* y *PDF*; cuatro tarjetas
 * (**total de compras**, **total de productos**, **monto en soles** y **monto en
 * dólares**); la tabla *Detalle de Compras por Producto* con 17 columnas (ID,
 * fecha, comprobante, tipo, proveedor, RUC, producto, código, cantidad, precio
 * unitario, básico, descuento, IGV, total, moneda, estado y estado de pago) y el
 * pie con la suma de cantidad, básico, descuento, IGV y total.
 *
 * Reglas conservadas:
 * - Por defecto se consulta **del primer día del mes en curso hasta hoy** y con el
 *   estado **Validado**, como los valores iniciales del formulario anterior.
 * - Se listan las **líneas de compra** (`compra_producto`) de las compras del
 *   periodo, con los mismos filtros y el mismo orden (fecha y compra descendente,
 *   producto).
 * - Las tarjetas cuentan las compras y los productos **distintos** del listado y
 *   separan los montos en soles y en dólares; el pie suma las líneas.
 *
 * Diferencias anotadas:
 * - Las fechas y los selectores usan los controles propios del panel
 *   (`CampoFecha` y `Select`) en lugar de los nativos con select2.
 * - La tabla de 17 columnas se comprime a 8: el número de compra, el RUC y el
 *   código van debajo de su nombre y el total lleva al lado el básico, el IGV y el
 *   descuento (así no hay que desplazarse a la derecha). El básico, el descuento,
 *   el IGV, el ISC, el ROD, el flete, el total de línea, la orden de compra, el
 *   SCOP y la guía **siguen en la exportación**, que trae las mismas columnas que
 *   el Excel anterior.
 * - Se añade el atajo de **meses con compras** y el aviso cuando el rango elegido
 *   no tiene datos (el histórico de esta copia termina en julio 2026).
 */

import { ArrowLeft, Download, FileText, Info, RefreshCw, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Paginador } from '@/components/ui/paginador';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { usePaginacion } from '@/hooks/use-paginacion';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatCurrency, formatDateValue, formatNumber } from '@/lib/utils';

import {
  reportesApi,
  useCatalogosCompras,
  useCompras,
  type FiltrosCompras,
  type LineaCompra,
} from '../reportes.api';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const CONTROL =
  'h-11 w-full rounded-control border border-ink-200 bg-white px-3 text-sm text-ink-900 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Fecha local `AAAA-MM-DD` (no usar `toISOString`, que cambia de día). */
function fechaLocal(fecha: Date): string {
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');

  return `${fecha.getFullYear()}-${mes}-${dia}`;
}

/** Rango por defecto del panel anterior: del día 1 del mes en curso a hoy. */
function rangoPorDefecto(): FiltrosCompras {
  const hoy = new Date();

  return {
    fecha_inicio: `${fechaLocal(hoy).slice(0, 7)}-01`,
    fecha_fin: fechaLocal(hoy),
    estado: '1',
    proveedor_id: null,
    producto_id: null,
  };
}

/** «julio 2026» a partir de `2026-07`. */
function etiquetaMes(periodo: string | null | undefined): string {
  if (!periodo || !/^\d{4}-\d{2}$/.test(periodo)) return periodo ?? '';

  const [anio, mes] = periodo.split('-');
  const nombre = MESES[Number(mes) - 1];

  return nombre ? `${nombre} ${anio}` : periodo;
}

/** Importe con el símbolo de su moneda, como el panel anterior. */
function monto(valor: number | null | undefined, moneda: string | null | undefined): string {
  if (moneda === 'DOLARES') return `$ ${formatNumber(valor ?? 0)}`;

  return formatCurrency(valor ?? 0);
}

export function ReporteComprasPage() {
  useDocumentTitle('Reportes · Compras por producto');

  const [filtros, setFiltros] = useState<FiltrosCompras>(rangoPorDefecto);
  const [aviso, setAviso] = useState<string | null>(null);

  const catalogos = useCatalogosCompras();
  const consulta = useCompras(filtros);
  const datos = consulta.data;
  const resumen = datos?.resumen;

  const meses = catalogos.data?.meses ?? [];
  const ultimoMesConDatos = meses[0]?.periodo ?? null;
  const sinCompras = Boolean(
    resumen && resumen.lineas === 0 && ultimoMesConDatos && !filtros.fecha_inicio.startsWith(ultimoMesConDatos),
  );

  const compras = useMemo(() => datos?.compras ?? [], [datos]);
  // El listado se pagina en el navegador; los repartos y el pie siguen usando el listado
  // completo.
  const paginacion = usePaginacion(compras, { clave: filtros });

  const cambiar = <C extends keyof FiltrosCompras>(campo: C, valor: FiltrosCompras[C]) =>
    setFiltros((previos) => ({ ...previos, [campo]: valor }));

  /** Atajo: ver todo el mes indicado. */
  const verMes = (periodo: string) => {
    const anio = Number(periodo.slice(0, 4));
    const mes = Number(periodo.slice(5, 7));
    const ultimoDia = new Date(anio, mes, 0).getDate();

    setFiltros((previos) => ({
      ...previos,
      fecha_inicio: `${periodo}-01`,
      fecha_fin: `${periodo}-${String(ultimoDia).padStart(2, '0')}`,
    }));
  };

  const exportar = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.exportarCompras(filtros), {
        nombre: `reporte-compras-${filtros.fecha_inicio}_${filtros.fecha_fin}.csv`,
      });
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo exportar el reporte.');
    }
  };

  /** Abre el PDF del reporte (mismo listado, para imprimir o archivar). */
  const exportarPdf = async () => {
    setAviso(null);

    try {
      await abrirPdfAutenticado(reportesApi.pdfCompras(filtros));
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo generar el PDF.');
    }
  };

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Link
            to="/reportes"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-700 hover:underline"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            Reportes
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-ink-900">Compras por producto</h1>
          <p className="mt-1 text-sm text-ink-500">
            Detalle de las compras del periodo, línea por producto y proveedor.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setFiltros(rangoPorDefecto())}
            leftIcon={<RefreshCw className="size-4" aria-hidden="true" />}
          >
            Limpiar
          </Button>
          <Button
            variant="outline"
            onClick={exportarPdf}
            leftIcon={<FileText className="size-4" aria-hidden="true" />}
          >
            PDF
          </Button>

          <Button
            variant="outline"
            onClick={exportar}
            leftIcon={<Download className="size-4" aria-hidden="true" />}
          >
            Exportar
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-4 p-4">
          <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Desde</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.fecha_inicio}
                  onChange={(evento) => cambiar('fecha_inicio', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </div>

            <div className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Hasta</span>
              <span className="flex h-11 items-center rounded-control border border-ink-200 bg-white focus-within:border-brand-400 focus-within:ring-4 focus-within:ring-brand-100">
                <CampoFecha
                  tipo="date"
                  value={filtros.fecha_fin}
                  onChange={(evento) => cambiar('fecha_fin', evento.target.value)}
                  className="w-full px-3 text-sm"
                />
              </span>
            </div>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Estado</span>
              <Select
                value={filtros.estado}
                onChange={({ target }) => cambiar('estado', target.value)}
                className={CONTROL}
                aria-label="Estado de la compra"
              >
                {(catalogos.data?.estados ?? []).map((estado) => (
                  <option key={estado.valor} value={estado.valor}>
                    {estado.etiqueta}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Proveedor</span>
              <Select
                value={filtros.proveedor_id === null ? '' : String(filtros.proveedor_id)}
                onChange={({ target }) =>
                  cambiar('proveedor_id', target.value === '' ? null : Number(target.value))
                }
                className={CONTROL}
                aria-label="Proveedor"
              >
                <option value="">Todos los proveedores</option>
                {(catalogos.data?.proveedores ?? []).map((proveedor) => (
                  <option key={proveedor.id} value={proveedor.id}>
                    {proveedor.nombre}
                    {proveedor.ruc ? ` - ${proveedor.ruc}` : ''}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1.5 text-sm">
              <span className="font-medium text-ink-700">Producto</span>
              <Select
                value={filtros.producto_id === null ? '' : String(filtros.producto_id)}
                onChange={({ target }) =>
                  cambiar('producto_id', target.value === '' ? null : Number(target.value))
                }
                className={CONTROL}
                aria-label="Producto"
              >
                <option value="">Todos los productos</option>
                {(catalogos.data?.productos ?? []).map((producto) => (
                  <option key={producto.id} value={producto.id}>
                    {producto.nombre}
                  </option>
                ))}
              </Select>
            </label>
          </div>

          {meses.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 border-t border-ink-100 pt-3">
              <span className="mr-1 inline-flex items-center gap-1.5 text-xs font-semibold text-ink-500">
                <Search className="size-3.5" aria-hidden="true" />
                Meses con compras
              </span>
              {meses.slice(0, 12).map((mes) => (
                <button
                  key={mes.periodo}
                  type="button"
                  aria-pressed={filtros.fecha_inicio.startsWith(mes.periodo)}
                  onClick={() => verMes(mes.periodo)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors',
                    filtros.fecha_inicio.startsWith(mes.periodo)
                      ? 'border-brand-300 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
                  )}
                  title={`${formatNumber(mes.compras)} compra(s) · ${formatCurrency(mes.total)}`}
                >
                  {etiquetaMes(mes.periodo)}
                </button>
              ))}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {consulta.isError ? (
        <Alert variant="error" title="No se pudo cargar el reporte">
          {consulta.error instanceof ApiError
            ? consulta.error.message
            : 'Inténtalo de nuevo en unos segundos.'}
        </Alert>
      ) : null}

      {aviso ? (
        <Alert variant="error" title="No se pudo exportar">
          {aviso}
        </Alert>
      ) : null}

      {sinCompras ? (
        <Alert variant="info" title="El rango elegido no tiene compras">
          <p>
            Esta copia tiene compras en {meses.length} mes(es) y el último es{' '}
            {etiquetaMes(ultimoMesConDatos)}.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => ultimoMesConDatos && verMes(ultimoMesConDatos)}
          >
            Ver {etiquetaMes(ultimoMesConDatos)}
          </Button>
        </Alert>
      ) : null}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          {
            etiqueta: 'Compras',
            valor: formatNumber(resumen?.compras ?? 0),
            detalle: `${formatNumber(resumen?.validados ?? 0)} validada(s) · ${formatNumber(
              resumen?.anulados ?? 0,
            )} anulada(s)`,
          },
          {
            etiqueta: 'Productos',
            valor: formatNumber(resumen?.productos ?? 0),
            detalle: `${formatNumber(resumen?.lineas ?? 0)} línea(s)`,
          },
          {
            etiqueta: 'Monto en soles',
            valor: formatCurrency(resumen?.monto_soles ?? 0),
            detalle: `${formatNumber(resumen?.cantidad ?? 0)} unidad(es)`,
          },
          {
            etiqueta: 'Monto en dólares',
            valor: `$ ${formatNumber(resumen?.monto_dolares ?? 0)}`,
            detalle: 'Sólo las compras facturadas en dólares',
          },
        ].map((tarjeta) => (
          <Card key={tarjeta.etiqueta}>
            <CardContent className="p-4">
              <p className="text-xs font-semibold tracking-wide text-ink-500 uppercase">
                {tarjeta.etiqueta}
              </p>
              <p className="text-lg font-bold text-ink-900">{tarjeta.valor}</p>
              <p className="text-xs text-ink-500">{tarjeta.detalle}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-ink-800">
              Detalle de compras por producto ({formatNumber(resumen?.lineas ?? 0)})
            </h2>
            <span className="text-xs text-ink-500">
              {formatDateValue(datos?.filtros.inicio)} – {formatDateValue(datos?.filtros.fin)} ·{' '}
              {catalogos.data?.estados.find((estado) => estado.valor === filtros.estado)?.etiqueta ??
                'Todos'}
            </span>
          </div>

          {consulta.isLoading ? <Skeleton className="h-64 w-full" /> : null}

          {!consulta.isLoading ? (
            <>
            <div className="max-h-[60vh] overflow-auto rounded-card border border-ink-100">
              <table className="w-full max-lg:min-w-[49rem] table-fixed border-collapse text-[13px]">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="w-[5.5rem] px-1.5 py-2">Fecha</th>
                    <th className="w-[6rem] px-1.5 py-2">Comprobante</th>
                    <th className="w-[7.5rem] px-1.5 py-2">Proveedor</th>
                    <th className="w-[6.25rem] px-1.5 py-2">Producto</th>
                    <th className="w-[3.5rem] px-1.5 py-2 text-right">Cant.</th>
                    <th className="w-[4.5rem] px-1.5 py-2 text-right">P. unit.</th>
                    <th className="w-[6.5rem] px-1.5 py-2 text-right">Total</th>
                    <th className="w-[5rem] px-1.5 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {compras.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={8} className="px-1.5 py-10 text-center text-sm text-ink-500">
                        No hay compras que cumplan los filtros.
                      </td>
                    </tr>
                  ) : null}

                  {paginacion.visibles.map((fila: LineaCompra, indice) => (
                    <tr
                      key={`${fila.compra_id}-${fila.producto_id ?? 'x'}-${indice}`}
                      className="border-t border-ink-100 hover:bg-brand-50/30"
                    >
                      <td className="px-1.5 py-2">
                        <p className="truncate font-semibold text-ink-900">
                          {formatDateValue(fila.fecha_emision)}
                        </p>
                        <p className="truncate text-[11px] text-ink-400">#{fila.compra_id}</p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate font-medium text-ink-800">{fila.comprobante ?? '—'}</p>
                        <p className="truncate text-[11px] text-ink-500">
                          {fila.tipo_comprobante ?? ''}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.proveedor ?? ''}>
                          {fila.proveedor ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">{fila.ruc_proveedor ?? ''}</p>
                      </td>
                      <td className="px-1.5 py-2">
                        <p className="truncate text-ink-800" title={fila.producto ?? ''}>
                          {fila.producto ?? '—'}
                        </p>
                        <p className="truncate text-[11px] text-ink-500">{fila.codigo ?? ''}</p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-700">
                        <p className="truncate">{formatNumber(fila.cantidad ?? 0)}</p>
                      </td>
                      <td className="px-1.5 py-2 text-right text-ink-600">
                        <p className="truncate">{formatNumber(fila.precio_unidad ?? 0)}</p>
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        <p className="truncate font-semibold text-ink-900">
                          {monto(fila.total, fila.moneda)}
                        </p>
                        <p
                          className="truncate text-[11px] text-ink-500"
                          title={`Básico ${formatNumber(fila.basico ?? 0)} · IGV ${formatNumber(
                            fila.igv ?? 0,
                          )} · Dscto ${formatNumber(fila.dscto ?? 0)}`}
                        >
                          B {formatNumber(fila.basico ?? 0)} · I {formatNumber(fila.igv ?? 0)}
                          {(fila.dscto ?? 0) > 0 ? ` · D ${formatNumber(fila.dscto ?? 0)}` : ''}
                        </p>
                      </td>
                      <td className="px-1.5 py-2">
                        <Badge
                          variant={fila.estado_codigo === 1 ? 'success' : 'danger'}
                          className="px-1.5 py-0 text-[10px]"
                        >
                          {fila.estado}
                        </Badge>
                        <p className="truncate text-[11px] text-ink-500">{fila.estado_pago}</p>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {compras.length > 0 ? (
                  <tfoot className="sticky bottom-0 bg-white shadow-[0_-1px_0_0_var(--color-ink-100)]">
                    <tr className="text-[13px] font-semibold text-ink-800">
                      <td className="px-1.5 py-2" colSpan={4}>
                        TOTALES · {formatNumber(resumen?.compras ?? 0)} compra(s)
                      </td>
                      <td className="px-1.5 py-2 text-right">
                        {formatNumber(resumen?.cantidad ?? 0)}
                      </td>
                      <td className="px-1.5 py-2" />
                      <td className="px-1.5 py-2 text-right">
                        <p className="truncate">{formatCurrency(resumen?.total ?? 0)}</p>
                        <p
                          className="truncate text-[11px] font-normal text-ink-500"
                          title={`Básico ${formatNumber(resumen?.basico ?? 0)} · IGV ${formatNumber(
                            resumen?.igv ?? 0,
                          )} · Dscto ${formatNumber(resumen?.dscto ?? 0)}`}
                        >
                          B {formatNumber(resumen?.basico ?? 0)} · I {formatNumber(resumen?.igv ?? 0)}
                        </p>
                      </td>
                      <td className="px-1.5 py-2" />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>

            <Paginador {...paginacion} etiqueta="compras" />
            </>
          ) : null}
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Cada fila es una línea de la compra (el total lleva al lado el básico «B», el IGV «I» y el
        descuento «D») y el pie suma el periodo. La exportación trae las veinte columnas del Excel
        anterior, incluidas la orden de compra, el SCOP y la guía de remisión.
      </p>
    </div>
  );
}
